import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getOrderList,
  fetchPaymentList,
  activateQR,
  deactivateQR,
  downloadOrderZIP,
  updateOrderDownloadStatus,
} from "../../redux/actions/orderActions";
import {
  approveOrder,
  processOrder,
} from "../../redux/actions/OrderApproveActions";
import DashboardLayout from "../../Pages/dashboard/DashboardLayout";
import MultiSelectDropdown from "../../Pages/Components/MultiSelectDropdown";
import SearchBar from "../../Pages/Components/SearchBar";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import clsx from "clsx";
import SortableTable from "../../Pages/Components/SortableTable";
import { FiDownload } from "react-icons/fi";
import { FaEdit, FaDownload, FaArrowLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
const OrderList = () => {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.auth);
  const { loading, error, orders, download_url } = useSelector(
    (state) => state.orderList
  );

  const [processedOrders, setProcessedOrders] = useState({});
  const [approvingOrders, setApprovingOrders] = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [extraAmounts, setExtraAmounts] = useState({});
  const [transactionAmounts, setTransactionAmounts] = useState({});
  const { payments } = useSelector((state) => state.paymentList);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [selectedClients, setSelectedClients] = useState([]);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [isActivated, setIsActivated] = React.useState(false);

  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [showActivateModal, setShowActivateModal] = React.useState(false);
  const [activationData, setActivationData] = React.useState({
    startDate: "",
    endDate: "",
  });
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [orderToDeactivate, setOrderToDeactivate] = useState(null);
  const [orderTypeFilter] = useState("all"); // Always show all orders, no filtering needed
  const [updatingDownloadStatus, setUpdatingDownloadStatus] = useState({});

  const navigate = useNavigate();

  const fixDate = (date) => {
    const ms = date.getTime() - date.getTimezoneOffset() * 60000;
    return new Date(ms).toISOString().split("T")[0];
  };

  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const [yyyy, mm, dd] = dateStr.split("-");
    return new Date(`${yyyy}-${mm}-${dd}`);
  };
  useEffect(() => {
    if (userInfo?.access_token) {
      dispatch(fetchPaymentList(userInfo.access_token));
    }
  }, [dispatch, userInfo?.access_token]);
  useEffect(() => {
    if (userInfo?.access_token) {
      dispatch(getOrderList(userInfo.access_token));
    }
  }, [dispatch, userInfo]);

  const handleProcessOrder = async (orderId, defaultAmount) => {
    if (selectedOrder?.order_id !== orderId) {
      setSelectedOrder({ order_id: orderId, order_amount: defaultAmount });

      setTransactionAmounts((prev) => ({
        ...prev,
        [orderId]: prev[orderId] ?? defaultAmount,
      }));
      setExtraAmounts((prev) => ({
        ...prev,
        [orderId]: 0,
      }));
      return;
    }

    const baseAmount = parseFloat(transactionAmounts[orderId] || defaultAmount);
    const extraAmount = parseFloat(extraAmounts[orderId] || 0);
    const totalAmount = baseAmount + extraAmount;

    try {
      setApprovingOrders((prev) => ({ ...prev, [orderId]: true }));
      await dispatch(processOrder(orderId, totalAmount, userInfo.access_token));
      await Promise.all([
        dispatch(getOrderList(userInfo.access_token)),
        dispatch(fetchPaymentList(userInfo.access_token)),
      ]);

      setProcessedOrders((prev) => ({ ...prev, [orderId]: true }));
      setSelectedOrder(null);
      setTransactionAmounts((prev) => ({ ...prev, [orderId]: "" }));
      setExtraAmounts((prev) => ({ ...prev, [orderId]: "" }));
    } catch (err) {
      console.error("Processing failed", err);
    } finally {
      setApprovingOrders((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  const handleApproveOrder = async (orderId) => {
    try {
      setApprovingOrders((prev) => ({ ...prev, [orderId]: true }));
      await dispatch(approveOrder(orderId, userInfo.access_token));
      await Promise.all([
        dispatch(getOrderList(userInfo.access_token)),
        dispatch(fetchPaymentList(userInfo.access_token)),
      ]);
    } catch (err) {
      console.error("Approval failed", err);
    } finally {
      setApprovingOrders((prev) => ({ ...prev, [orderId]: false }));
    }
  };
  const downloadFile = (url) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = ""; // optionally, set a filename here
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownload = async () => {
    try {
      const response = await dispatch(
        getOrderList(userInfo.access_token, { download: true })
      );
      const fileUrl = response?.data?.download_url;
      if (fileUrl) {
        downloadFile(fileUrl);
      } else {
        console.warn("No download URL in response");
      }
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  const handleActivateSubmit = async () => {
    const { orderId, startDate, endDate } = activationData;

    if (!startDate || !endDate) {
      alert("Please select both start and end dates.");
      return;
    }

    const payload = {
      order_id: orderId,
      start_date: startDate.split("-").reverse().join("-"), // DD-MM-YYYY
      end_date: endDate.split("-").reverse().join("-"),
    };

    try {
      await dispatch(activateQR(payload, userInfo.access_token));
      await dispatch(getOrderList(userInfo.access_token));

      // 👇 Update the selected order's state so the UI reflects activation
      setSelectedOrder((prev) => ({
        ...prev,
        is_active: true,
      }));
      // setIsActivated(true);
      setShowActivateModal(false);
    } catch (err) {
      alert("Failed to activate QR.");
    }
  };

  const handleDeactivateClick = (order) => {
    if (!order?.order_id || !userInfo?.access_token) return;

    dispatch(deactivateQR(order.order_id, userInfo.access_token));
    setSelectedOrder((prev) => ({
      ...prev,
      is_active: false,
    }));
  };

  const handleDownloadQR = (orderId) => {
    dispatch(downloadOrderZIP(orderId, userInfo.access_token));
  };

  const handleUpdateDownloadStatus = async (orderId, currentStatus, e) => {
    if (e) {
      e.stopPropagation();
    }
    
    if (!userInfo?.access_token) {
      alert("Authentication token missing");
      return;
    }

    const newStatus = !currentStatus;
    setUpdatingDownloadStatus((prev) => ({ ...prev, [orderId]: true }));

    try {
      await dispatch(updateOrderDownloadStatus(orderId, newStatus, userInfo.access_token));
      toast.success(`Download status updated to ${newStatus ? "Downloaded" : "Not Downloaded"}`);
      // Refresh order list
      dispatch(getOrderList(userInfo.access_token));
    } catch (error) {
      toast.error(error.message || "Failed to update download status");
    } finally {
      setUpdatingDownloadStatus((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  // const columns = [
  //   { key: "order_time", label: "Date" },
  //   { key: "order_id", label: "Order ID" },
  //   { key: "client_name", label: "Client Name" },
  //   { key: "order_amount", label: "Order Amount" },
  //   { key: "qr_quantity", label: "QR Quantity" },
  //   { key: "status", label: "Status" },
  //   { key: "Sheme", label: "Scheme" },
  // ];
  const baseColumns = [
    { key: "order_time", label: "Date" },
    { key: "order_id", label: "Order ID" },
    { key: "order_type", label: "Order Type" },
    { key: "client_name", label: "Client Name" },
    // { key: "order_amount", label: "Order Amount" },
    { key: "qr_quantity", label: "QR Quantity" },
    { key: "status", label: "Status" },
    { key: "is_downloaded", label: "Download Status" },
  ];

  const schemeColumn = { key: "scheme", label: "Scheme" };

  // Always include scheme column since we're showing all orders
  // Add download status column for role 6 users
  const isRole6 = useMemo(() => {
    const roleId = userInfo?.role_id;
    return roleId === "6" || roleId === 6 || roleId === "06";
  }, [userInfo?.role_id]);

  const columns = useMemo(() => {
    const cloned = [...baseColumns];
    const statusIndex = cloned.findIndex((col) => col.key === "status");
    cloned.splice(statusIndex, 0, schemeColumn); // Insert before status
    
    // Add download status column after status for role 6
    if (isRole6) {
      // Find status column index again (after scheme was inserted)
      const statusColIndex = cloned.findIndex((col) => col.key === "status");
      if (statusColIndex !== -1) {
        cloned.splice(statusColIndex + 1, 0, downloadStatusColumn);
      }
    }
    
    return cloned;
  }, [isRole6]);

  const statusOptions = [
    { value: "All", label: "All" },
    { value: "Payment Pending", label: "Payment Pending" },
    { value: "Approval Pending", label: "Waiting For Approve" },
    { value: "Successful", label: "Approved" },
    { value: "Pending Payment", label: "Process Order" },
  ];
  const clientOptions = [
    { value: "All", label: "All" },
    ...Array.from(new Set(orders?.map((o) => o.client_name)))
      .filter(Boolean)
      .map((name) => ({ value: name, label: name })),
  ];

  const filteredOrders = orders
    ?.filter((order) => {
      if (userInfo.role_id === "6") return true;
      return order.user_id === userInfo.user_id;
    })
    ?.filter((order) => {
      if (selectedStatuses.length === 0) return true;

      const payment = payments?.data?.find(
        (p) => p.order_id === order.order_id
      );

      return selectedStatuses.some(({ value }) => {
        if (value === "Pending Payment") return order.status === value;
        if (value === "All") return true;
        return payment?.status === value;
      });
    })
    ?.filter((order) => {
      if (
        selectedClients.length === 0 ||
        selectedClients.some((c) => c.value === "All")
      )
        return true;
      return selectedClients.some(({ value }) => value === order.client_name);
    })
    ?.filter((order) => {
      const search = searchValue.trim().toLowerCase();

      const orderId = order.order_id?.toLowerCase() || "";
      const clientName = order.client_name?.toLowerCase() || "";
      const numericOnly = orderId.replace(/\D/g, ""); // removes all non-digits

      return (
        orderId.includes(search) || // match full/partial text with # or CM
        numericOnly.includes(search) || // match number-only search like 8650
        clientName.includes(search)
      );
    })
    // Removed order type filter - always show all orders

    // ?.filter((order) => {
    //   const search = searchValue.trim().toLowerCase();
    //   return (
    //     order.order_id.toString().includes(search) ||
    //     order.client_name?.toLowerCase().includes(search)
    //   );
    // })

    .filter((order) => {
      if (!startDate && !endDate) return true;

      const rawDate = order.order_time?.trim();

      // Parse DD-MM-YYYY manually to ISO (YYYY-MM-DD)
      const [day, month, year] = rawDate.split("-");
      if (!day || !month || !year) {
        console.warn(
          "Malformed date string:",
          rawDate,
          "for order ID:",
          order.order_id
        );
        return false;
      }

      const formattedDate = `${year}-${month}-${day}`;
      const orderDate = dayjs(formattedDate);

      if (!orderDate.isValid()) {
        console.warn(
          "Parsed invalid date:",
          formattedDate,
          "for order ID:",
          order.order_id
        );
        return false;
      }

      const start = startDate ? dayjs(startDate).startOf("day") : null;
      const end = endDate ? dayjs(endDate).endOf("day") : null;

      if (start && orderDate.isBefore(start)) return false;
      if (end && orderDate.isAfter(end)) return false;

      return true;
    });

  return (
    <DashboardLayout>
      <div className="mt-2 max-w-full mx-2 ">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 mb-2">
            <button
              onClick={() => navigate(-1)}
              className="hover:text-gray-800"
            >
              <FaArrowLeft size={20} />
            </button>
            <h2 className="text-2xl font-bold text-amber-600 mb-1">
              Order List
            </h2>
          </div>

          <button
            onClick={handleDownload}
            className="bg-white flex items-center px-4 py-2 text-sm font-medium text-black border border-gray-500 rounded-md transition"
          >
            <FiDownload className="w-4 h-4 mr-2 text-amber-500" />
            Export
          </button>
        </div>

        {loading ? (
          <div>Loading...</div>
        ) : error ? (
          <div className="text-red-600">{error}</div>
        ) : (
          <div className="overflow-x-auto">
            <div className="mt-0 w-3/4 flex flex-col sm:flex-row items-center justify-start gap-4 ">
              <SearchBar
                className=""
                value={searchValue}
                onChange={setSearchValue}
                placeholder="Search by Order ID / Client "
              />

              {/* <MultiSelectDropdown
                options={statusOptions}
                selectedValues={selectedStatuses}
                setSelectedValues={setSelectedStatuses}
                placeholder="Filter by Status"
              /> */}

              <MultiSelectDropdown
                className="w-full "
                options={clientOptions}
                selectedValues={selectedClients}
                setSelectedValues={setSelectedClients}
                placeholder="Filter by Client"
              />

              <DatePicker
                selectsRange
                startDate={startDate}
                endDate={endDate}
                onChange={(update) => setDateRange(update)}
                isClearable
                placeholderText="Select date range"
                dateFormat="dd MMM yyyy"
                className="w-1.4 px-4 py-2 border border-gray-950 rounded-md text-sm placeholder-gray-950"
              />
            </div>
            {(selectedStatuses.length > 0 || selectedClients.length > 0) && (
              <div className="w-full flex gap-6">
                {selectedStatuses.length > 0 && (
                  <div className="">
                    <div className="text-md font-bold text-gray-950 py-2">
                      Selected Status:
                    </div>
                    <div className="flex flex-wrap gap-4">
                      {selectedStatuses.map((status) => (
                        <span
                          key={status.value}
                          className="bg-amber-300 text-gray-950 px-3 py-2 rounded-full text-sm font-medium"
                        >
                          {status.label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {selectedClients.length > 0 && (
                  <div className="">
                    <div className="text-md font-bold text-gray-950 py-2">
                      Selected Clients:
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedClients.map((client) => (
                        <span
                          key={client.value}
                          className="bg-amber-300 text-gray-950 px-3 py-2 rounded-full text-sm font-medium"
                        >
                          {client.label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="relative">
              <div className="overflow-x-auto mt-4 rounded-lg ">
                <SortableTable
                  columns={columns}
                  data={filteredOrders}
                  renderRow={(order) => {
                    const payment = payments?.data?.find(
                      (p) => p.order_id === order.order_id
                    );

                    const getStatusButton = () => {
                      const baseClass =
                        "px-4 py-1 text-sm font-semibold rounded-full text-white  transition duration-200";

                      if (order.status === "Pending Payment") {
                        return (
                          <button
                            onClick={() => setSelectedOrder(order)}
                            className={`${baseClass} bg-red-500 hover:bg-red-600`}
                          >
                            Process Order
                          </button>
                        );
                      } else if (
                        order.is_approved ||
                        payment?.order_is_approved
                      ) {
                        return (
                          <button
                            onClick={() => setSelectedOrder(order)}
                            className={`${baseClass} bg-green-600 hover:bg-green-700`}
                          >
                            Approved
                          </button>
                        );
                      } else if (
                        payment?.status === "Approval Pending" ||
                        order?.status === "Approval Pending"
                      ) {
                        return (
                          <button
                            onClick={() => setSelectedOrder(order)}
                            className={`${baseClass} bg-yellow-500 hover:bg-yellow-600`}
                          >
                            Approval Pending
                          </button>
                        );
                      } else if (
                        order.status === "In Processing" &&
                        payment?.status === "Payment Pending"
                      ) {
                        return (
                          <button
                            onClick={() => setSelectedOrder(order)}
                            className={`${baseClass} bg-orange-500 hover:bg-orange-600`}
                          >
                            Payment Pending
                          </button>
                        );
                      } else {
                        return (
                          <button
                            onClick={() => setSelectedOrder(order)}
                            className={`${baseClass} bg-blue-500 hover:bg-blue-600`}
                          >
                            {payment?.status || "Status"}
                          </button>
                        );
                      }
                    };

                    return (
                      <tr
                        key={order.order_id}
                        onClick={() => {
                          setSelectedOrder(order);
                          setStatusClicked(false); // Optional, if you need this flag
                        }}
                        className="hover:bg-amber-50 hover:text-gray-950 hover:font-semibold"
                      >
                        <td className="px-2 py-4 border-b-gray-400">
                          {order.order_time}
                        </td>
                        <td className="px-2 py-4 border-b-gray-400">
                          {order.order_id}
                        </td>
                        <td className="px-2 py-4 border-b-gray-400">
                          {order.order_type}
                        </td>

                        <td className="px-2 py-4 border-b-gray-400">
                          {order.client_name}
                        </td>
                        {/* <td className="px-2 py-4 border-b-gray-400">
                          ₹{order.order_amount}
                        </td> */}
                        <td className="px-2 py-4 border-b-gray-400">
                          {order.qr_quantity}
                        </td>
                        <td className="px-2 py-4 border-b-gray-400">
                          {/* Show combined scheme list if multiple in breakdowns */}
                          {order.breakdowns
                            ?.filter((b) => b.scheme)
                            .map((b) => b.scheme)
                            .join(", ") || "-"}
                        </td>
                        <td className="px-2 py-4 border-b-gray-400">
                          {getStatusButton()}
                        </td>

                          <td className="px-2 py-4 border-b-gray-400 text-center">
                            <button
                              onClick={(e) => {
                                handleUpdateDownloadStatus(
                                  order.order_id,
                                  order.is_downloaded,
                                  e
                                );
                              }}
                              disabled={updatingDownloadStatus[order.order_id]}
                              className={`py-1 px-3 rounded-full text-sm font-medium transition ${
                                order.is_downloaded
                                  ? "bg-green-500 hover:bg-green-600 text-white"
                                  : "bg-gray-300 hover:bg-gray-400 text-gray-700"
                              } ${updatingDownloadStatus[order.order_id] ? "opacity-50 cursor-not-allowed" : ""}`}
                              title={order.is_downloaded ? "Mark as Not Downloaded" : "Mark as Downloaded"}
                            >
                              {updatingDownloadStatus[order.order_id] ? (
                                "Updating..."
                              ) : order.is_downloaded ? (
                                "Downloaded"
                              ) : (
                                "Not Downloaded"
                              )}
                            </button>
                          </td>

                      </tr>
                    );
                  }}
                />
              </div>

              {/* Order Details Sidebar (unchanged UI, but cleaner spacing) */}
              {selectedOrder && (
                <>
                  {/* Overlay */}
                  <div
                    onClick={() => setSelectedOrder(null)}
                    className="fixed inset-0 bg-black bg-opacity-40 z-40"
                  />

                  {/* Side Panel */}
                  <div className="fixed top-0 right-0 w-full sm:w-[600px] h-full bg-white z-50 overflow-y-auto transition-transform duration-300">
                    {/* Header */}
                    <div className="flex justify-between items-center px-5 py-4 border-b bg-amber-50">
                      <h3 className="text-lg font-bold text-gray-950">
                        Order Details
                      </h3>
                      <button
                        onClick={() => setSelectedOrder(null)}
                        className="text-gray-500 hover:text-red-500 text-xl"
                      >
                        &times;
                      </button>
                    </div>

                    {/* Body */}
                    <div className="p-5 text-sm text-gray-700 space-y-6">
                      {/* Order Info Section */}
                      <div className="space-y-2">
                        <h2 className="text-lg font-bold text-amber-600">
                          Order Info
                        </h2>
                        <div className="bg-amber-100 p-4 rounded-md space-y-2 border border-amber-600">
                          <div className="flex justify-between text-left">
                            <span className="block text-sm text-gray-900  font-bold tracking-wide mb-1">
                              Order ID
                            </span>
                            <span className="text-md font-semibold text-gray-900">
                              {selectedOrder.order_id}
                            </span>
                          </div>
                          <div className="flex justify-between  text-left">
                            <span className="block text-sm text-gray-900 font-bold tracking-wide mb-1">
                              Client Name
                            </span>
                            <span className="text-md font-semibold text-gray-900">
                              {selectedOrder.client_name}
                            </span>
                          </div>
                          {/* <div className="flex justify-between  text-left">
                            <span className="block text-sm text-gray-900 font-bold tracking-wide mb-1">
                              Firm Name
                            </span>
                            <span className="text-md font-semibold text-gray-900">
                              {selectedOrder.firm_Name}
                            </span>
                          </div> */}
                          <div className="flex justify-between text-left">
                            <span className="block text-sm text-gray-900 font-bold tracking-wide mb-1">
                              Order Date
                            </span>
                            <span className="text-md font-semibold text-gray-900">
                              {dayjs(selectedOrder.order_date).format(
                                "DD-MM-YYYY"
                              )}
                            </span>
                          </div>
                          <div className="flex justify-between text-left">
                            <span className="block text-sm text-gray-900 font-bold tracking-wide mb-1">
                              QR Quantity
                            </span>
                            <span className="text-md font-semibold text-gray-900">
                              {selectedOrder.qr_quantity}
                            </span>
                          </div>
                        
                       
                        </div>
                      </div>
                      {/* Payment Info Section */}
                      {/* {(() => {
                        const payment = payments?.data?.find(
                          (p) => p.order_id === selectedOrder.order_id
                        );
                        return payment ? (
                          <div className="space-y-2">
                            <h2 className="text-lg font-bold text-amber-600">
                              Payment Info
                            </h2>
                            <div className="bg-amber-100 p-4 rounded-md space-y-2 border border-amber-600">
                              <div className="flex justify-between text-left">
                                <span className="block text-sm text-gray-900 font-bold tracking-wide mb-1">
                                  Status
                                </span>
                                <span
                                  className={`py-1 px-3 rounded-full text-sm font-semibold ${
                                    payment.status === "Successful"
                                      ? "bg-green-600 text-white"
                                      : payment.status === "Approval Pending"
                                      ? "bg-yellow-600 text-white"
                                      : "bg-red-600 text-white"
                                  }`}
                                >
                                  {payment.status}
                                </span>
                              </div>
                              <div className="flex justify-between text-left">
                                <span className="block text-sm text-gray-900  font-bold  tracking-wide mb-1">
                                  Order Amount
                                </span>
                                <span className="text-md text-start font-semibold text-gray-900">
                                  {payment.order_amount ?? 0}
                                </span>
                              </div>
                              <div className="flex justify-between text-left">
                                <span className="block text-sm text-gray-900  font-bold  tracking-wide mb-1">
                                  Transaction Amount
                                </span>
                                <span className="text-md text-start font-semibold text-gray-900">
                                  {payment.extra_charge ?? 0}
                                </span>
                              </div>
                              <div className="flex justify-between text-left">
                                <span className="block text-sm text-gray-900  font-bold  tracking-wide mb-1">
                                  Total Amount
                                </span>
                                <span className="text-md text-start font-semibold text-gray-900">
                                  {payment.amount ?? 0}
                                </span>
                              </div>

                              {(payment.status === "Successful" ||
                                payment.status === "Approval Pending") && (
                                <div className="space-y-2">
                                  <div className="flex justify-between text-left">
                                    <span className="block text-sm text-gray-900 font-bold tracking-wide">
                                      Payment Mode
                                    </span>
                                    <span className="text-md text-start font-semibold text-gray-900">
                                      {payment.payment_type}
                                    </span>
                                  </div>
                                  <div className="flex justify-between text-left">
                                    <span className="block text-sm text-gray-900 font-bold tracking-wide">
                                      UTR
                                    </span>
                                    <span className="text-md text-start font-semibold text-gray-900">
                                      {payment.utr_number}
                                    </span>
                                  </div>
                                  <div className="flex justify-between text-left">
                                    <span className="block text-sm text-gray-900 font-bold tracking-wide">
                                      Date
                                    </span>
                                    <span className="text-md text-start font-semibold text-gray-900">
                                      {payment.payment_date}
                                    </span>
                                  </div>
                                </div>
                              )}

                              {payment.status === "Approval Pending" &&
                                selectedOrder.status === "Approval Pending" && (
                                  <div className="flex justify-end mt-4">
                                    <button
                                      onClick={() =>
                                        handleApproveOrder(
                                          selectedOrder.order_id,
                                          {
                                            order_amount:
                                              selectedOrder.order_amount,
                                            extra_amount:
                                              extraAmounts[
                                                selectedOrder.order_id
                                              ] || 0,
                                          }
                                        )
                                      }
                                      className="flex flex-col justify-center items-center  bg-green-600 hover:bg-green-600 text-white px-4 py-2 rounded-md font-semibold  mt-2"
                                    >
                                      Confirm Approval
                                    </button>
                                  </div>
                                )}
                            </div>
                            {payment?.status === "Successful" &&
                              selectedOrder?.is_approved && (
                                <div className="mt-4">
                                  {selectedOrder?.is_active ? (
                                    <div className="flex justify-end gap-2">
                                  
                                      <span className="bg-green-800 text-white font-semibold py-2 px-4 rounded-md cursor-default">
                                        Activated
                                      </span>

                                    
                              
                                      <button
                                        onClick={() => {
                                          setOrderToDeactivate(selectedOrder);
                                          setShowDeactivateModal(true);
                                        }}
                                        className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-md"
                                      >
                                        Deactivate
                                      </button>

                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDownloadQR(
                                            selectedOrder.order_id
                                          );
                                        }}
                                        className="bg-indigo-500 text-white py-1 px-3 rounded-full text-sm"
                                      >
                                        <FaDownload />
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="flex justify-end">
                                      <button
                                        onClick={() => {
                                          setActivationData({
                                            orderId: selectedOrder.order_id,
                                            startDate: "",
                                            endDate: "",
                                          });
                                          setShowActivateModal(true);
                                        }}
                                        className=" flex justify-end bg-green-600 hover:bg-green-600  mt-2 text-white font-bold py-2 px-4 rounded-md"
                                      >
                                        Activate Coupon
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                          </div>
                        ) : (
                          <div className="text-red-500">
                            No payment data available.
                          </div>
                        );
                      })()} */}
                      {(() => {
                        const payment = payments?.data?.find(
                          (p) => p.order_id === selectedOrder.order_id
                        );

                        return (
                          <>
                            {/* ✅ Payment Block (shown only if exists) */}
                            {payment ? (
                              <div className="space-y-2">
                                <h2 className="text-lg font-bold text-amber-600">
                                  Payment Info
                                </h2>

                                <div className="bg-amber-100 p-4 rounded-md space-y-2 border border-amber-600">
                                  {/* Payment Status */}
                                  <div className="flex justify-between text-left">
                                    <span className="block text-sm font-bold text-gray-900 mb-1">
                                      Status
                                    </span>
                                    <span
                                      className={`py-1 px-3 rounded-full text-sm font-semibold ${
                                        payment.status === "Successful"
                                          ? "bg-green-600"
                                          : payment.status ===
                                            "Approval Pending"
                                          ? "bg-yellow-600"
                                          : "bg-red-600"
                                      } text-white`}
                                    >
                                      {payment.status}
                                    </span>
                                  </div>

                                  {/* Other payment fields */}
                                  <div className="flex justify-between text-left">
                                    <span className="block text-sm font-bold text-gray-900 mb-1">
                                      Order Amount
                                    </span>
                                    <span className="text-md font-semibold text-gray-900">
                                      {payment.order_amount ?? 0}
                                    </span>
                                  </div>

                                  <div className="flex justify-between text-left">
                                    <span className="block text-sm font-bold text-gray-900 mb-1">
                                      Transaction Amount
                                    </span>
                                    <span className="text-md font-semibold text-gray-900">
                                      {payment.extra_charge ?? 0}
                                    </span>
                                  </div>

                                  <div className="flex justify-between text-left">
                                    <span className="block text-sm font-bold text-gray-900 mb-1">
                                      Total Amount
                                    </span>
                                    <span className="text-md font-semibold text-gray-900">
                                      {payment.amount ?? 0}
                                    </span>
                                  </div>

                                  {/* Optional payment info */}
                                  {(payment.status === "Successful" ||
                                    payment.status === "Approval Pending") && (
                                    <div className="space-y-2">
                                      <div className="flex justify-between text-left">
                                        <span className="block text-sm font-bold text-gray-900">
                                          Payment Mode
                                        </span>
                                        <span className="text-md font-semibold text-gray-900">
                                          {payment.payment_type}
                                        </span>
                                      </div>
                                      <div className="flex justify-between text-left">
                                        <span className="block text-sm font-bold text-gray-900">
                                          UTR
                                        </span>
                                        <span className="text-md font-semibold text-gray-900">
                                          {payment.utr_number}
                                        </span>
                                      </div>
                                      <div className="flex justify-between text-left">
                                        <span className="block text-sm font-bold text-gray-900">
                                          Date
                                        </span>
                                        <span className="text-md font-semibold text-gray-900">
                                          {payment.payment_date}
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                  {payment?.status === "Successful" &&
                                    selectedOrder?.is_approved && (
                                      <div className="mt-4">
                                        {selectedOrder?.is_active ? (
                                          <div className="flex justify-end gap-2">
                                            <span className="bg-green-800 text-white font-semibold py-2 px-4 rounded-md cursor-default">
                                              Activated
                                            </span>

                                            <button
                                              onClick={() => {
                                                setOrderToDeactivate(
                                                  selectedOrder
                                                );
                                                setShowDeactivateModal(true);
                                              }}
                                              className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-md"
                                            >
                                              Deactivate
                                            </button>

                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleDownloadQR(
                                                  selectedOrder.order_id
                                                );
                                              }}
                                              className="bg-indigo-500 text-white py-1 px-3 rounded-full text-sm"
                                            >
                                              <FaDownload />
                                            </button>
                                          </div>
                                        ) : (
                                          <div className="flex justify-end">
                                            <button
                                              onClick={() => {
                                                setActivationData({
                                                  orderId:
                                                    selectedOrder.order_id,
                                                  startDate: "",
                                                  endDate: "",
                                                });
                                                setShowActivateModal(true);
                                              }}
                                              className=" flex justify-end bg-green-600 hover:bg-green-600  mt-2 text-white font-bold py-2 px-4 rounded-md"
                                            >
                                              Activate Coupon
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                </div>
                              </div>
                            ) 
                            : 
                            (
                              <div className="text-red-500">
                                {/* No payment data available. */}
                              </div>
                            )
                            
                            }

                            {/* ✅ Always check selectedOrder.status separately */}
                            {selectedOrder?.status === "Approval Pending" && (
                              <div className="flex justify-end mt-4">
                                <button
                                  onClick={() =>
                                    handleApproveOrder(selectedOrder.order_id, {
                                      order_amount: selectedOrder.order_amount,
                                      extra_amount:
                                        extraAmounts[selectedOrder.order_id] ||
                                        0,
                                    })
                                  }
                                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-semibold"
                                >
                                  Confirm Approval
                                </button>
                              </div>
                            )}
                          </>
                        );
                      })()}

                      {/* Transaction Entry Section */}
                      {selectedOrder.status === "Pending Payment" && (
                        <div className="space-y-3">
                          <div>
                            <label className="block font-medium mb-1">
                              Transaction Amount
                            </label>
                            <input
                              type="number"
                              placeholder="Enter extra charge"
                              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              value={extraAmounts[selectedOrder.order_id] || ""}
                              onChange={(e) =>
                                setExtraAmounts((prev) => ({
                                  ...prev,
                                  [selectedOrder.order_id]: e.target.value,
                                }))
                              }
                            />
                          </div>

                          <button
                            onClick={() =>
                              handleProcessOrder(
                                selectedOrder.order_id,
                                selectedOrder.order_amount,
                                extraAmounts[selectedOrder.order_id] || 0
                              )
                            }
                            disabled={approvingOrders[selectedOrder.order_id]}
                            className={`${
                              approvingOrders[selectedOrder.order_id]
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-red-500 hover:bg-red-600"
                            } text-white px-4 py-2 rounded-md font-semibold w-full`}
                          >
                            {approvingOrders[selectedOrder.order_id]
                              ? "Processing..."
                              : "Confirm Process"}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      {showActivateModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-amber-100 border border-amber-500 p-8 rounded-xl shadow-2xl w-full max-w-md mx-4 animate-fadeIn">
            <h2 className="text-lg font-bold text-amber-600 mb-6 text-center">
              Activate QR Code
            </h2>

            {/* <label className="block mb-2 font-medium">Start Date</label>
            <input
              type="date"
              value={activationData.startDate}
              onChange={(e) =>
                setActivationData((prev) => ({
                  ...prev,
                  startDate: e.target.value,
                }))
              }
              className="w-full border border-gray-300 rounded px-3 py-2 mb-4"
            />

            <label className="block mb-2 font-medium">End Date</label>
            <input
              type="date"
              value={activationData.endDate}
              onChange={(e) =>
                setActivationData((prev) => ({
                  ...prev,
                  endDate: e.target.value,
                }))
              }
              className="w-full border border-gray-300 rounded px-3 py-2 mb-4"
            /> */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              {/* Start Date */}
              <div className="w-full">
                <label className="ml-4 block mb-1 text-sm text-gray-950 font-medium">
                  Start Date
                </label>
                <DatePicker
                  selected={parseDate(activationData.startDate)}
                  onChange={(date) =>
                    setActivationData({
                      ...activationData,
                      // startDate: date.toISOString().split("T")[0],
                      startDate: fixDate(date),
                    })
                  }
                  dateFormat="dd-MM-yyyy"
                  className="w-full p-2 border border-amber-500 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholderText="dd/mm/yyyy"
                />
              </div>

              {/* End Date */}
              <div className="w-full">
                <label className="ml-4  block mb-1 text-sm text-gray-950 font-medium">
                  End Date
                </label>
                <DatePicker
                  selected={parseDate(activationData.endDate)}
                  onChange={(date) =>
                    setActivationData({
                      ...activationData,
                      // endDate: date.toISOString().split("T")[0],
                      endDate: fixDate(date),
                    })
                  }
                  dateFormat="dd-MM-yyyy"
                  className="w-full p-2 border border-amber-500 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholderText="dd/mm/yyyy"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowActivateModal(false)}
                className="px-4 py-2 text-white bg-red-500 rounded hover:bg-red-500"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleActivateSubmit(); // Call the function
                  setShowActivateModal(false);
                }}
                disabled={!activationData.startDate || !activationData.endDate}
                className={`px-4 py-2 rounded text-white ${
                  activationData.startDate && activationData.endDate
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-green-600 cursor-not-allowed"
                }`}
              >
                Activate
              </button>
            </div>
          </div>
        </div>
      )}
      {showDeactivateModal && orderToDeactivate && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-xl shadow-lg w-full max-w-sm mx-4">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              Confirm Deactivation
            </h3>
            <p className="mb-4 text-gray-950">
              Are you sure you want to deactivate this order? <br />
              <span className="font-semibold">Unused QR Codes:</span>{" "}
              {orderToDeactivate.unused_codes}
            </p>

            {/* <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to deactivate it? This action can't be
              undone.
            </p> */}

            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowDeactivateModal(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleDeactivateClick(orderToDeactivate);
                  setShowDeactivateModal(false);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default OrderList;
